import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useSearchParams } from 'react-router-dom';
import { Alert, Button, Card, Container, Group, Skeleton, Stack, Text } from '@mantine/core';
import PageTitle from '@/components/common/PageTitle';
import { IconAlertTriangle, IconPlus, IconSearch, IconWheat } from '@tabler/icons-react';
import EmptyState from '@/components/common/EmptyState';
import BuyFilterBar from '@/components/app/buy/BuyFilterBar';
import SellBasisCard from '@/components/app/sell/SellBasisCard';
import AgencyGateBanner from '@/components/app/sell/AgencyGateBanner';
import SellAttestationGate from '@/components/app/sell/SellAttestationGate';
import BestPriceAlertCard from '@/components/app/sell/BestPriceAlertCard';
import { useAgencyAgreementMissed, useSaleTree, useStartSellOffer } from '@/hooks/useSell';
import { useSaleBestOffersMap } from '@/hooks/useBestOffers';
import { useAttestationRequired } from '@/hooks/useAttestationRequired';
import { useIsAgent } from '@/hooks/useRoles';
import { flattenSaleTree, groupSellByBasis } from '@/lib/sellTree';
import { isServiceUnavailable } from '@/api/errors';
import { ROUTES } from '@/routes/paths';
import { formatOrgName } from '@/lib/format';

const basisKey = (group: { address: string; addressTitle: string }): string =>
  `${group.address}|${group.addressTitle}`;

// «Продам» — мои офферы на продажу: карточки базисов → строки-культуры → (раскрытие) офферы-лоты.
// Гейт агентского договора специфичен этому разделу (баннер + блок создания), «Купить» не трогает.
export default function SellListPage() {
  const { t } = useTranslation();
  const { data, isLoading, isError, error, refetch } = useSaleTree();
  const isAgent = useIsAgent(); // агент не публикует предложения о продаже (только посредничает)
  // Предвыбор фильтра из query (после создания предложения / из «Лучшей цены») — фильтр переживает навигацию.
  const [params] = useSearchParams();
  const initAddress = params.get('address');
  const initAddressTitle = params.get('addressTitle');
  const [basis, setBasis] = useState<string | null>(
    initAddress && initAddressTitle ? `${initAddress}|${initAddressTitle}` : null,
  );
  const [good, setGood] = useState<string | null>(params.get('goods'));

  const agencyMissed = useAgencyAgreementMissed();
  const attestationRequired = useAttestationRequired();
  const startCreate = useStartSellOffer();
  const bestOffers = useSaleBestOffersMap();

  // Создавать предложение может только продавец (не агент) с пройденной аттестацией И подписанным
  // агентским договором. Если чего-то не хватает — кнопку не показываем вовсе (а не редиректим по клику):
  // нужный призыв к действию уже даёт баннер выше (SellAttestationGate / AgencyGateBanner).
  const canCreate = !isAgent && !attestationRequired && !agencyMissed;

  const groups = useMemo(() => flattenSaleTree(data), [data]);
  const allBases = useMemo(() => groupSellByBasis(groups), [groups]);
  const basisOptions = useMemo(
    () =>
      allBases.map((base) => ({
        value: basisKey(base),
        label: `${formatOrgName(base.addressTitle)} — ${base.address}`,
        title: base.addressTitle,
        address: base.address,
      })),
    [allBases],
  );
  const goodsOptions = useMemo(() => [...new Set(groups.map((group) => group.goods))].sort(), [groups]);

  const filtered = useMemo(
    () => groups.filter((group) => (!basis || basisKey(group) === basis) && (!good || group.goods === good)),
    [groups, basis, good],
  );
  const basisCards = useMemo(() => groupSellByBasis(filtered), [filtered]);
  const showFilters = allBases.length > 1 || goodsOptions.length > 1;

  return (
    <Container size="lg" py="xl">
      <Stack gap="lg">
        {/* wrap: на узком экране кнопки действий переносятся под заголовок, а не сжимают его в столбик. */}
        <Group justify="space-between" wrap="wrap" gap="md" align="flex-start">
          <Stack gap={4} style={{ flex: '1 1 260px', minWidth: 0 }}>
            <PageTitle>{t('app.sell.title')}</PageTitle>
            <Text size="sm" c="gray.8">
              {t('app.sell.subtitle')}
            </Text>
          </Stack>
          <Group gap="sm" wrap="wrap" style={{ flexShrink: 0, maxWidth: '100%' }}>
            <Button component={Link} to={ROUTES.APP_SELL_BEST_PRICE} variant="default" leftSection={<IconSearch size={16} />}>
              {t('app.sell.bestPrice')}
            </Button>
            {groups.length > 0 && canCreate ? (
              <Button leftSection={<IconPlus size={16} />} onClick={startCreate}>
                {t('app.sell.newOffer')}
              </Button>
            ) : null}
          </Group>
        </Group>

        {/* Аттестация — базовое требование, показываем её приоритетнее агентского договора. */}
        {attestationRequired && !isAgent ? <SellAttestationGate /> : agencyMissed ? <AgencyGateBanner /> : null}

        {!isAgent && !agencyMissed && groups.length > 0 ? <BestPriceAlertCard /> : null}

        {isError ? (
          <Alert color="red" variant="light" icon={<IconAlertTriangle size={18} />}>
            <Group justify="space-between" wrap="nowrap">
              <Text>{isServiceUnavailable(error) ? t('auth.errors.unavailable') : t('auth.errors.generic')}</Text>
              <Button variant="subtle" size="compact-sm" onClick={() => refetch()}>
                {t('common.retry')}
              </Button>
            </Group>
          </Alert>
        ) : isLoading ? (
          <Stack gap="sm">
            <Skeleton h={140} />
            <Skeleton h={140} />
          </Stack>
        ) : groups.length === 0 ? (
          <Card withBorder padding="lg">
            {isAgent ? (
              <EmptyState
                decorated={false}
                icon={<IconWheat size={30} />}
                title={t('app.sell.agentTitle')}
                description={t('app.sell.agentHint')}
              />
            ) : (
              <EmptyState
                decorated={false}
                icon={<IconWheat size={30} />}
                title={t('app.sell.empty')}
                description={t('app.sell.emptyHint')}
                action={
                  canCreate ? (
                    <Button leftSection={<IconPlus size={16} />} onClick={startCreate}>
                      {t('app.sell.create')}
                    </Button>
                  ) : undefined
                }
              />
            )}
          </Card>
        ) : (
          <Stack gap="md">
            {showFilters ? (
              <Group justify="space-between" wrap="wrap" align="center" gap="sm">
                <BuyFilterBar
                  bases={basisOptions}
                  goods={goodsOptions}
                  basis={basis}
                  good={good}
                  onBasis={setBasis}
                  onGood={setGood}
                />
                <Text size="sm" c="gray.8">
                  {t('app.sell.bases', { count: basisCards.length })}
                </Text>
              </Group>
            ) : null}
            {basisCards.length === 0 ? (
              <Group gap="xs">
                <Text c="gray.7" size="sm">
                  {t('app.sell.nothingFound')}
                </Text>
                <Button
                  variant="subtle"
                  size="compact-xs"
                  onClick={() => {
                    setBasis(null);
                    setGood(null);
                  }}
                >
                  {t('app.sell.resetFilters')}
                </Button>
              </Group>
            ) : (
              <Stack gap="md">
                {basisCards.map((basisGroup) => (
                  <SellBasisCard key={basisKey(basisGroup)} basis={basisGroup} good={good} bestOffers={bestOffers} />
                ))}
              </Stack>
            )}
          </Stack>
        )}
      </Stack>
    </Container>
  );
}
